import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import { toast } from '../ui/Toast';
import { Icon, Sparkles } from '../ui/Icon';
import { expensesApi, type ExtractedExpense } from '../../api/expenses.api';
import { useAuthStore } from '../../stores/auth.store';
import { withinLimit, uploadErrorMessage } from '../../utils/uploadLimits';
import type { Expense, Trip } from '../../types';
import ScannedExpensesReview from './ScannedExpensesReview';

// The server reads at most three files per request, and a scrolled statement is
// normally two or three screenshots — so that cap is also the useful one here.
const MAX_FILES = 3;
const ACCEPT = 'image/*,application/pdf';

interface Props {
  tripId: string;
  trip: Trip;
  /** Rows handed over by a form scan that already ran; omit to start at the picker. */
  initialExpenses?: ExtractedExpense[] | null;
  onClose: () => void;
  onSaved: (created: Expense[]) => void;
}

/** Upload a receipt or a statement screenshot, then review every expense found. */
export default function ScanExpensesModal({ tripId, trip, initialExpenses, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { updateAiCredits, updatePremiumAiCredits } = useAuthStore();
  const [expenses, setExpenses] = useState<ExtractedExpense[] | null>(initialExpenses ?? null);
  const [scanning, setScanning] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  async function handleFiles(fileList: FileList | File[] | null) {
    const picked = Array.from(fileList ?? []).slice(0, MAX_FILES);
    if (picked.length === 0) return;
    const { accepted, errors } = withinLimit(picked, 'media');
    errors.forEach(message => toast(message, 'error'));
    if (accepted.length === 0) return;
    setScanning(true);
    try {
      const data = await expensesApi.extract(tripId, accepted);
      updateAiCredits(data.aiCreditsLeft);
      updatePremiumAiCredits(data.premiumAiCreditsLeft);
      setExpenses(data.expenses);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast(
        status === 402
          ? t('billing.aiPaywall.error')
          : uploadErrorMessage(error, accepted[0].name, 'media', t('expenses.scan.failed')),
        'error',
      );
    } finally {
      setScanning(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t('expenses.scan.title')} size="lg">
      {expenses ? (
        <ScannedExpensesReview
          tripId={tripId}
          trip={trip}
          expenses={expenses}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : scanning ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Spinner size="lg" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('expenses.scan.scanning')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('expenses.scan.explanation')}</p>
          <label
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors text-center ${
              dragOver
                ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-600 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
            }`}
          >
            <input
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
            />
            <Icon as={Sparkles} size={22} className="text-teal-500" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('expenses.scan.dropTitle')}</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">{t('expenses.scan.dropSubtitle', { count: MAX_FILES })}</p>
          </label>
        </div>
      )}
    </Modal>
  );
}
